import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import FiltersBar from "./FiltersBar";

const getImg = (p) =>
  (p?.media?.images && p.media.images[0]?.url) ||
  (Array.isArray(p?.images) && p.images[0]) ||
  p?.image ||
  "/img/placeholder.webp";

const inStockOf = (p) =>
  (p?.stock?.inStock !== undefined ? p.stock.inStock : p?.inStock) ?? true;

const getCompat = (p) => (p && typeof p === "object" ? p.compat || {} : {});
const protocolsOf = (p) => getCompat(p).protocols || [];
const ecosystemsOf = (p) => getCompat(p).ecosystems || {};
const hasDirectControl = (p) => !!(getCompat(p).directControl?.available);
const tagsOf = (p) => (Array.isArray(p?.tags) ? p.tags : []);
const getRequiresHubs = (p) => {
  const raw = getCompat(p).requiresHubs;
  if (Array.isArray(raw)) return raw;
  return raw ? [raw] : [];
};
const getCompatibleHubs = (p) => {
  const raw = getCompat(p).compatibleHubs;
  if (Array.isArray(raw)) return raw;
  return raw ? [raw] : [];
};

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("hu-HU") + " Ft" : "");

export default function ProductPicker({
  title,
  products,
  quantitiesById = {},
  onChangeQty,
  onPrev,
  onNext,
  fmtFt = fmt,
  functionId,
  ecosystem,
  prefs,
  hubs = [],
  relatedFiles = [],
  showNav = true, // ⬅️ ÚJ: belső navigáció opcionális
}) {
  const facets = useMemo(() => {
    const brands = new Set();
    const protocols = new Set();
    const tags = new Set();
    let min = Number.POSITIVE_INFINITY;
    let max = 0;

    for (const p of products) {
      if (p.brand) brands.add(p.brand);
      for (const pr of protocolsOf(p)) if (pr) protocols.add(pr);
      for (const t of tagsOf(p)) if (t) tags.add(t);
      const price = typeof p.price === "number" ? p.price : 0;
      if (price > 0) {
        if (price < min) min = price;
        if (price > max) max = price;
      }
    }
    if (!Number.isFinite(min)) min = 0;

    return {
      brands: Array.from(brands).sort(),
      protocols: Array.from(protocols).sort(),
      tags: Array.from(tags).sort(),
      price: { min, max },
      ecosystems: true,
    };
  }, [products]);

  const ecoKey =
    ecosystem === "apple" ? "homekit"
      : ecosystem === "google" ? "google"
      : ecosystem === "alexa" ? "alexa"
      : ecosystem === "matter" ? "matter"
      : "";

  const wizardSelectedHubId =
    prefs?.hasHub === "yes" && prefs.existingHubId ? prefs.existingHubId : undefined;

  const prefillFromWizard = useMemo(() => {
    const base = { sort: "relevance" };
    if (ecoKey) base.ecosystems = { [ecoKey]: true };
    if (prefs?.hubPolicy === "no-hub-only") base.directControl = true;
    if (wizardSelectedHubId) base.selectedHubId = wizardSelectedHubId;
    return base;
  }, [ecoKey, prefs?.hubPolicy, wizardSelectedHubId]);

  const storageKey = functionId ? `shb:filters:${functionId}` : null;
  const [filters, setFilters] = useState(() => {
    let saved = { sort: "relevance" };
    if (storageKey) {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) saved = JSON.parse(raw);
      } catch {}
    }
    return {
      ...saved,
      ...prefillFromWizard,
      ecosystems: { ...(saved.ecosystems || {}), ...(prefillFromWizard.ecosystems || {}) },
    };
  });

  useEffect(() => {
    setFilters((curr) => ({
      ...curr,
      ...prefillFromWizard,
      ecosystems: { ...(curr.ecosystems || {}), ...(prefillFromWizard.ecosystems || {}) },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecoKey, prefs?.hubPolicy, wizardSelectedHubId]);

  useEffect(() => {
    if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [filters, storageKey]);

  const clearFilters = () => setFilters({ sort: "relevance" });

  const selectedHubName =
    hubs.find((h) => h.id === filters.selectedHubId)?.name ||
    (wizardSelectedHubId && hubs.find((h) => h.id === wizardSelectedHubId)?.name) ||
    "";

  const wizardSummaryChips = useMemo(() => {
    const chips = [];
    if (ecoKey) {
      chips.push(
        ecoKey === "homekit" ? "Ökoszisztéma: Apple (HomeKit)"
          : ecoKey === "google" ? "Ökoszisztéma: Google Home"
          : ecoKey === "alexa" ? "Ökoszisztéma: Amazon Alexa"
          : "Ökoszisztéma: Matter"
      );
    }
    if (filters.selectedHubId) chips.push(`Kiválasztott HUB: ${selectedHubName}`);
    return chips;
  }, [ecoKey, filters.selectedHubId, selectedHubName]);

  const filtered = useMemo(() => {
    const q = (filters.q || "").trim().toLowerCase();
    const hubId = filters.selectedHubId;

    let list = products.filter((p) => {
      if (hubId) {
        const req = getRequiresHubs(p);
        const comp = getCompatibleHubs(p);
        if (!((Array.isArray(req) && req.includes(hubId)) || (Array.isArray(comp) && comp.includes(hubId)))) {
          return false;
        }
      }

      if (q) {
        const hay = `${p.name || ""} ${p.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      const price = typeof p.price === "number" ? p.price : 0;
      if (filters.priceMin !== undefined && price < filters.priceMin) return false;
      if (filters.priceMax !== undefined && price > filters.priceMax) return false;
      if (filters.onlyInStock && !inStockOf(p)) return false;

      if (Array.isArray(filters.brands) && filters.brands.length > 0) {
        if (!filters.brands.includes(p.brand)) return false;
      }
      if (Array.isArray(filters.protocols) && filters.protocols.length > 0) {
        const ps = protocolsOf(p);
        if (!ps || !ps.some((pr) => filters.protocols.includes(pr))) return false;
      }
      if (filters.ecosystems && Object.values(filters.ecosystems).some(Boolean)) {
        const eco = ecosystemsOf(p);
        for (const k of ["homekit", "google", "alexa", "matter"]) {
          if (filters.ecosystems[k] && !eco?.[k]) return false;
        }
      }

      if (!hubId && filters.directControl && !hasDirectControl(p)) return false;

      if (Array.isArray(filters.tags) && filters.tags.length > 0) {
        const t = tagsOf(p);
        if (!t || !filters.tags.every((tt) => t.includes(tt))) return false;
      }

      return true;
    });

    const sort = filters.sort || "relevance";
    if (sort === "price-asc") list = list.slice().sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === "price-desc") list = list.slice().sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sort === "name-asc") list = list.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sort === "name-desc") list = list.slice().sort((a, b) => (b.name || "").localeCompare(a.name || ""));

    return list;
  }, [products, filters]);

  const [showFilters, setShowFilters] = useState(false);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.selectedHubId) chips.push(`HUB: ${selectedHubName}`);
    if (filters.q) chips.push(`Keresés: “${filters.q}”`);
    if (filters.onlyInStock) chips.push("Raktáron");
    if (filters.priceMin !== undefined) chips.push(`Min: ${filters.priceMin} Ft`);
    if (filters.priceMax !== undefined) chips.push(`Max: ${filters.priceMax} Ft`);
    if (filters.brands?.length) chips.push(...filters.brands.map((b) => `Márka: ${b}`));
    if (filters.protocols?.length) chips.push(...filters.protocols.map((p) => `Proto: ${p}`));
    if (filters.ecosystems && Object.values(filters.ecosystems).some(Boolean)) {
      for (const k of ["homekit", "google", "alexa", "matter"]) {
        if (filters.ecosystems[k]) chips.push(`Ökoszisztéma: ${k}`);
      }
    }
    if (!filters.selectedHubId && filters.directControl) chips.push("App-vezérlés");
    if (filters.tags?.length) chips.push(...filters.tags.map((t) => `Címke: ${t}`));
    return chips;
  }, [filters, selectedHubName]);

  return (
    <>
      <section className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">{title} – termékek</h2>
          <div className="text-sm text-gray-600">{filtered.length} találat</div>
        </div>
        <p className="text-sm text-gray-600">
          A kiválasztott HUB alapján csak a vele kompatibilis termékek látszanak.
        </p>

        {Array.isArray(relatedFiles) && relatedFiles.length > 0 && (
          <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
            <div className="text-sm font-medium mb-1">
              Kapcsolódó levél kategória fájlok ehhez a funkcióhoz
            </div>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              {relatedFiles.map((f) => (
                <li key={f.filename}>
                  <code className="px-1.5 py-0.5 bg-gray-50 border rounded">{f.filename}</code>
                  <span className="text-gray-500"> – kategória: {f.categoryName}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-1">
              A fájl neve megegyezik a levél kategória nevével.
            </p>
          </div>
        )}

        {activeChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {activeChips.map((c, i) => (
              <span key={i} className="px-2 py-1 rounded-full text-xs bg-gray-100 border">
                {c}
              </span>
            ))}
          </div>
        )}
      </section>

      <div className="md:grid md:grid-cols-[16rem_1fr] gap-5">
        <div className="hidden md:block">
          <FiltersBar
            facets={facets}
            value={filters}
            onChange={setFilters}
            onClear={clearFilters}
            wizardSummaryChips={wizardSummaryChips}
            hubs={hubs}
          />
        </div>

        <div className="min-w-0">
          <div className="md:hidden mb-3">
            <button
              className="px-4 py-2 border rounded w-full"
              onClick={() => setShowFilters(true)}
            >
              Szűrők megnyitása
            </button>
          </div>

          {showFilters && (
            <div className="fixed inset-0 z-40 md:hidden">
              <div className="absolute inset-0 bg-black/30" onClick={() => setShowFilters(false)} />
              <div className="absolute right-0 top-0 h-full w-[85%] bg-white shadow-xl p-4">
                <FiltersBar
                  facets={facets}
                  value={filters}
                  onChange={setFilters}
                  onClear={clearFilters}
                  onClose={() => setShowFilters(false)}
                  wizardSummaryChips={wizardSummaryChips}
                  hubs={hubs}
                />
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <p className="text-gray-600">Nincs találat a jelenlegi szűrőkre.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, idx) => {
                const qty = Number(quantitiesById[p.id] || 0);
                const active = qty > 0;
                const imgSrc = getImg(p);
                const eco = ecosystemsOf(p);

                const hubId = filters.selectedHubId;
                const compatWithSelected =
                  !!hubId &&
                  (getRequiresHubs(p).includes(hubId) || getCompatibleHubs(p).includes(hubId));

                return (
                  <motion.div
                    key={p.id}
                    className={`bg-white rounded-2xl border ${
                      active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
                    } hover:border-gray-300 shadow-sm hover:shadow-md transition-all p-4`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.01 * idx, duration: 0.2 }}
                  >
                    <div className="aspect-[4/3] w-full overflow-hidden rounded-xl mb-3 bg-gray-50">
                      <img src={imgSrc} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                    </div>

                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-gray-600 line-clamp-2">{p.description}</div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {eco?.homekit && (
                        <span className="px-2 py-0.5 text-xs rounded bg-emerald-50 border border-emerald-200">
                          HomeKit
                        </span>
                      )}
                      {eco?.google && (
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-50 border border-blue-200">
                          Google
                        </span>
                      )}
                      {eco?.alexa && (
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-50 border border-indigo-200">
                          Alexa
                        </span>
                      )}
                      {eco?.matter && (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-50 border">Matter</span>
                      )}
                      {hubId && compatWithSelected && (
                        <span className="px-2 py-0.5 text-xs rounded bg-teal-50 border border-teal-200">
                          Kompatibilis a kiválasztott HUB-bal
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                      <span>{p.brand || "—"}</span>
                      <span className={inStockOf(p) ? "text-emerald-600" : "text-amber-600"}>
                        {inStockOf(p) ? "Raktáron" : "Rendelésre"}
                      </span>
                    </div>

                    <div className="text-sm font-medium mt-1">{fmtFt(p.price)}</div>

                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        className="h-9 w-9 border rounded hover:bg-gray-50"
                        onClick={() => onChangeQty(p.id, Math.max(0, qty - 1))}
                        aria-label="Egy darabbal kevesebb"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={0}
                        max={99}
                        value={Number.isFinite(qty) ? qty : 0}
                        onChange={(e) => onChangeQty(p.id, e.target.value)}
                        className="h-9 w-16 border rounded text-center"
                      />
                      <button
                        type="button"
                        className="h-9 w-9 border rounded hover:bg-gray-50"
                        onClick={() =>
                          onChangeQty(p.id, Math.min(99, (Number.isFinite(qty) ? qty : 0) + 1))
                        }
                        aria-label="Egy darabbal több"
                      >
                        +
                      </button>
                      {active && <span className="text-xs text-blue-600 ml-2">Kiválasztva</span>}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* BELSŐ NAV – csak ha szeretnénk megjeleníteni */}
          {showNav && (
            <div className="flex justify-between mt-6">
              <button className="border px-5 py-2 rounded hover:bg-gray-50" onClick={onPrev}>
                Vissza
              </button>
              <button className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700" onClick={onNext}>
                Tovább
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
